import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import DeliveryScheduleTableItem from 'components/delivery-schedule/delivery-schedule-table-item.component';
import { productionOverviewOrdersTemplate } from 'apollo/templates/production-overview';
import { Order } from 'models/order';
import { ProductionOverviewCellInput, ProductionOverviewCountMode } from 'models/production-overview';
import { printProductionOverviewOrders } from './print-production-overview-orders';
import OverviewOrdersDialogFilters from './overview-orders-dialog-filters.component';
import { EMPTY_ORDERS_FILTER, OverviewOrdersFilter, filterOverviewOrders } from './production-overview-utils';

export type OverviewOrdersDialogState = {
    /** The heading parts of what was clicked, e.g. ["Received", "Via scanner case", "2026-10-30"]. */
    labelParts: string[];
    /** One cell per disjoint count behind the clicked number; the lists are concatenated. */
    cells: ProductionOverviewCellInput[];
};

type OverviewOrdersDialogProps = {
    dialogState: OverviewOrdersDialogState;
    countMode: ProductionOverviewCountMode;
    onClose: () => void;
};

const OverviewOrdersDialog: FC<OverviewOrdersDialogProps> = ({
    dialogState,
    countMode,
    onClose,
}: OverviewOrdersDialogProps) => {
    const { t: translate } = useTranslation([
        'production-overview',
        'common',
        'orders',
        'delivery-schedule',
        'workflow',
    ]);
    const apolloClient = useApolloClient();
    const [orders, setOrders] = useState<Order[] | null>(null);
    const [loadError, setLoadError] = useState<Error | null>(null);
    const [filter, setFilter] = useState<OverviewOrdersFilter>(EMPTY_ORDERS_FILTER);

    // A clicked number may stand for several disjoint cells (a group total, a department
    // row); the click-through lists are always fetched live so they match the backend now.
    useEffect(() => {
        let isStale = false;
        setOrders(null);
        setLoadError(null);
        setFilter(EMPTY_ORDERS_FILTER);
        Promise.all(
            dialogState.cells.map((cell) =>
                apolloClient.query({
                    query: productionOverviewOrdersTemplate,
                    variables: { cell },
                    fetchPolicy: 'network-only',
                }),
            ),
        )
            .then((results) => {
                if (isStale) return;
                const ordersById = new Map<string, Order>();
                results
                    .flatMap((result) => (result.data?.productionOverviewOrders || []) as Order[])
                    .forEach((order) => ordersById.set(order._id, order));
                setOrders(Array.from(ordersById.values()));
            })
            .catch((error) => {
                if (!isStale) setLoadError(error);
            });
        return () => {
            isStale = true;
        };
    }, [dialogState]);

    const cameInViaLabelFor = (order: Order): string =>
        order.cameInVia ? translate(`production-overview:CAME_IN_VIA_${order.cameInVia}`) : '-';

    // Same step labels as the list rows, so the step filter reads like the column it filters.
    const stepLabelFor = (stepName: string): string => {
        if (stepName === 'NO_STEP') return translate('workflow:NO_STEP');
        if (stepName === 'ALL_STEPS_DONE') return translate('workflow:ALL_STEPS_DONE');
        return stepName || '-';
    };

    const filteredOrders = useMemo(() => filterOverviewOrders(orders || [], filter), [orders, filter]);
    const dialogTitle = dialogState.labelParts.join(' · ');
    const totalActionCount = filteredOrders.reduce((total, order) => total + (order.actionsCount || 0), 0);

    const countHeading =
        countMode === ProductionOverviewCountMode.ACTIONS
            ? translate('production-overview:DIALOG_ACTIONS_ACROSS_ORDERS', {
                  actionCount: totalActionCount,
                  orderCount: filteredOrders.length,
              })
            : translate('production-overview:DIALOG_ORDER_COUNT', { count: filteredOrders.length });

    const countAndPrint = orders ? (
        <Fragment>
            <Typography variant="body2" data-test="production-overview.dialog-count">
                {countHeading}
            </Typography>
            <Button
                startIcon={<PrintIcon />}
                disabled={!filteredOrders.length}
                onClick={() => printProductionOverviewOrders(filteredOrders, cameInViaLabelFor, dialogTitle, translate)}
                data-test="production-overview.dialog-print-button"
            >
                {translate('orders:PRINT')}
            </Button>
        </Fragment>
    ) : undefined;

    return (
        <DHDialog open fullScreen onClose={onClose} title={dialogTitle}>
            {!orders && !loadError ? <Loader /> : undefined}
            {loadError ? (
                <Typography variant="body2" color="error">
                    {translate('production-overview:DIALOG_LOAD_ERROR')}
                </Typography>
            ) : undefined}
            {orders ? (
                <Box>
                    {orders.length ? (
                        <OverviewOrdersDialogFilters
                            orders={orders}
                            filter={filter}
                            onFilterChange={setFilter}
                            stepLabelFor={stepLabelFor}
                        >
                            {countAndPrint}
                        </OverviewOrdersDialogFilters>
                    ) : (
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                            {countAndPrint}
                        </Box>
                    )}
                    {filteredOrders.length ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('common:ORDER_ID')}</TableCell>
                                    <TableCell>{translate('common:PATIENT')}</TableCell>
                                    <TableCell>{translate('delivery-schedule:DELIVERY_TO')}</TableCell>
                                    <TableCell>{translate('common:DENTIST')}</TableCell>
                                    <TableCell>{translate('orders:DELIVERY_DATE')}</TableCell>
                                    <TableCell>{translate('orders:WORKFLOW_STEP')}</TableCell>
                                    <TableCell>{translate('orders:CONSTRUCTIONS')}</TableCell>
                                    <TableCell>{translate('production-overview:CAME_IN_VIA')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredOrders.map((order) => (
                                    <DeliveryScheduleTableItem
                                        key={order._id}
                                        order={order}
                                        isExternal={false}
                                        readOnly
                                        cameInViaLabel={cameInViaLabelFor(order)}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {orders.length
                                ? translate('production-overview:DIALOG_NO_FILTER_MATCH')
                                : translate('orders:NO_ORDER_FOUND')}
                        </Typography>
                    )}
                </Box>
            ) : undefined}
        </DHDialog>
    );
};

export default OverviewOrdersDialog;
